<template>
  <div
    v-if="hasSlaThreshold"
    class="relative flex items-center border cursor-pointer min-w-fit bg-transparent"
    :class="[slaBorderStyles, showExtendedInfo ? 'rounded-lg mr-3' : 'rounded']"
  >
    <div
      v-on-clickaway="closeSlaPopover"
      class="flex items-center w-full truncate py-0.5"
      :class="showExtendedInfo ? 'px-1' : 'px-1.5 gap-0.5'"
      @mouseover="openSlaPopover()"
    >
      <div
        class="flex items-center gap-0.5"
        :class="
          showExtendedInfo &&
          'ltr:pr-1.5 rtl:pl-1.5 ltr:border-r rtl:border-l border-solid border-slate-100 dark:border-slate-700'
        "
      >
        <fluent-icon
          size="12"
          :icon="slaStatus.icon"
          type="outline"
          :icon-lib="isSlaMissed ? 'lucide' : 'fluent'"
          class="flex-shrink-0"
          :class="slaTextStyles"
        />
        <span
          v-if="showExtendedInfo"
          class="text-xs font-medium"
          :class="slaTextStyles"
        >
          {{ slaStatusText }}
        </span>
      </div>
      <span
        class="font-medium text-[#EF4444]"
        :class="[
          slaTextStyles,
          showExtendedInfo
            ? 'ltr:pl-1.5 rtl:pr-1.5 text-xs'
            : 'text-xxs/[14px]',
        ]"
      >
        {{ slaStatus.threshold }}
      </span>
    </div>
    <SLA-popover-card
      v-if="showSlaPopoverCard"
      :sla-missed-events="slaEvents"
      :chat="chat"
      class="right-0 top-7"
    />
  </div>
</template>

<script>
import { evaluateSLAStatus } from '@chatwoot/utils';
import SLAPopoverCard from './SLAPopoverCard.vue';

const REFRESH_INTERVAL = 30000;

export default {
  components: {
    SLAPopoverCard,
  },
  props: {
    chat: {
      type: Object,
      default: () => ({}),
    },
    showExtendedInfo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      timer: null,
      showSlaPopover: false,
      slaStatus: {
        threshold: null,
        isSlaMissed: false,
        type: null,
        icon: null,
      },
    };
  },
  computed: {
    slaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    appliedSLA() {
      const appliedSla = this.chat?.applied_sla;
      if (!appliedSla) return appliedSla;
      const sanitized = { ...appliedSla };
      [
        'sla_first_response_time_threshold',
        'sla_next_response_time_threshold',
        'sla_resolution_time_threshold',
      ].forEach(key => {
        if (!(sanitized[key] > 0)) sanitized[key] = null;
      });
      return sanitized;
    },
    slaEvents() {
      return this.chat?.sla_events_with_distinct_event_type;
    },
    hasSlaThreshold() {
      return this.slaStatus?.threshold;
    },
    isSlaMissed() {
      return this.slaStatus?.isSlaMissed;
    },
    slaBorderStyles() {
      return this.isSlaMissed ? 'border-red-300' : 'border-yellow-400';
    },
    slaTextStyles() {
      return this.isSlaMissed
        ? 'text-red-500 dark:text-red-500'
        : 'text-yellow-600 dark:text-yellow-500';
    },
    slaStatusText() {
      const upperCaseType = this.slaStatus?.type?.toUpperCase(); // FRT, NRT, or RT
      const statusKey = this.isSlaMissed ? 'MISSED' : 'DUE';

      return this.$t(`CONVERSATION.HEADER.SLA_STATUS.${upperCaseType}`, {
        status: this.$t(`CONVERSATION.HEADER.SLA_STATUS.${statusKey}`),
      });
    },
    showSlaPopoverCard() {
      return (
        this.showExtendedInfo && this.showSlaPopover && this.slaEvents.length
      );
    },
  },
  watch: {
    chat() {
      this.updateSlaStatus();
    },
  },
  mounted() {
    this.updateSlaStatus();
    this.createTimer();
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.updateSlaStatus();
        this.createTimer();
      }, REFRESH_INTERVAL);
    },
    updateSlaStatus() {
      const appliedSla = this.appliedSLA;
      const chat = this.chat;
      const status = evaluateSLAStatus({ appliedSla, chat });

      // Response-time SLAs (FRT/NRT) reflect the current reply obligation and
      // should take precedence over the long-running resolution-time (RT) SLA.
      // evaluateSLAStatus ranks by absolute remaining time, so on an aged
      // conversation RT can eclipse an active FRT/NRT (its remaining time keeps
      // shrinking from created_at while NRT resets on every waiting_since).
      // When RT wins, re-evaluate with RT suppressed so the active response SLA
      // surfaces; fall back to RT when no response SLA is currently active.
      if (status?.type === 'RT' && appliedSla) {
        const responseStatus = evaluateSLAStatus({
          appliedSla: { ...appliedSla, sla_resolution_time_threshold: null },
          chat,
        });
        if (responseStatus?.type) {
          this.slaStatus = responseStatus;
          return;
        }
      }

      this.slaStatus = status;
    },
    openSlaPopover() {
      if (!this.showExtendedInfo) return;
      // refresh the icon so it and the popover compute from the same moment
      this.updateSlaStatus();
      this.showSlaPopover = true;
    },
    closeSlaPopover() {
      this.showSlaPopover = false;
    },
  },
};
</script>
